import React, { useRef } from 'react';
import LoadingSpinner from '../LoadingSpinner';
import { SortIcons } from './Sort';
import ScrollButton from './ScrollButton';
import TitleWithHint from '../TitleWithHint';
import { cn } from '../../utils/cn';

/** Универсальный компонент таблицы
 * props:
  children - Пагинация
  columns - массив с ключами key, title, hint, render, handleSearch, sortable, sortKey
  data - данные с бэкенда
  handleRowClick - action для строки таблицы
  sortBy, sortOrder, onSort - для сортируемых колонок
  getRowClassName(row, index) - доп. классы строки

  hint - текст подсказки, рядом с заголовком появляется иконка info с раскрытием по клику

  если key = actions - в render передается вся строка
  для остальных колонок render вызывается как render(value, row)
  sortKey - переопределяет ключ сортировки (используется, когда col.key не совпадает с полем для сортировки, например key='actions')

 **/

const Table = (props) => {
  const { error, loading, columns, data, children, handleRowClick, classContainer, rowKey = 'id', sortBy, sortOrder, onSort, getRowClassName } = props;

  const scrollRef = useRef(null);

  if (loading) {
    return (
      <div className="text-gray-500">
        <LoadingSpinner style={'h-[500px] flex items-center justify-center'} loading={true} />
      </div>
    );
  }

  return (
    <div className={`relative max-w-7xl mx-auto bg-white shadow-md rounded-3xl p-4 pt-0 ${classContainer ?? 'mt-8'}`}>
      {error ? (
        <p className="text-red-500 pt-3 text-center">Ошибка: {error.error || 'Не удалось загрузить данные'}</p>
      ) : (
        <>
          <ScrollButton scrollRef={scrollRef} />

          <div ref={scrollRef} className="overflow-auto max-h-[70vh]">
            <table className="w-full border-collapse text-sm sm:text-base">
              <thead>
                <tr className="text-left bg-white text-gray-500 text-xs sm:text-sm border-b border-gray-200">
                  {columns.map((col, index) => {
                    const isSortable = col.sortable && onSort;
                    const sortColKey = col.sortKey ?? col.key;

                    return (
                      <th key={`${col.key}-${index}`} className={cn('sticky top-0 z-10 bg-white px-4 pt-6 pb-4 uppercase', col?.classHeader)}>
                        <div className="flex items-center gap-1">
                          <TitleWithHint title={col.title} hint={col.hint} />
                          {isSortable && <SortIcons sortBy={sortBy} sortOrder={sortOrder} colKey={sortColKey} onSort={onSort} />}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {!data || data?.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-gray-700 p-4 whitespace-nowrap">
                      Результаты не найдены
                    </td>
                  </tr>
                ) : (
                  data?.map((row, index) => {
                    const rowClass = getRowClassName?.(row, index);

                    return (
                      <tr
                        onClick={() => handleRowClick?.(row)}
                        key={row?.[rowKey] ?? index}
                        className={cn(
                          rowClass || (index % 2 === 0 ? 'bg-gray-50' : 'bg-white'),
                          handleRowClick && 'cursor-pointer',
                          !rowClass && 'hover:bg-gray-100',
                          'transition-colors'
                        )}>
                        {columns.map((col, index) => (
                          <td key={index} className={cn('py-2 px-4 text-gray-700', col.classHeader)}>
                            {col.render ? (col.key === 'actions' ? col.render(row) : col.render(row[col.key], row)) : row[col.key]}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {children && React.isValidElement(children) ? children : null}
        </>
      )}
    </div>
  );
};

export default Table;
